import { useMemo, useState, type DragEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../api';
import { useMeta } from '../meta';
import { Avatar, ErrorBox, PriorityIcon, TypeIcon, issueNo, statusClass, useIssueRowColor } from '../components';
import type { IssueHeader, IssueList } from '../types';
import { ViewSwitch } from './IssueList';
import { setLastList } from '../recent';
import { t } from '../i18n';

export function BoardPage() {
  const { meta, projectId, project, items, label } = useMeta();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [showClosed, setShowClosed] = useState(false);
  const [onlyMine, setOnlyMine] = useState(false);
  const [assignees, setAssignees] = useState<Set<string>>(new Set());
  const [text, setText] = useState('');
  const [dragId, setDragId] = useState<number | null>(null);
  const [overCol, setOverCol] = useState<number | null>(null);

  const statuses = items('status').filter((s) => showClosed || s.value < 90);
  const base = { project: projectId || undefined, per_page: 1000, sort: 'priority', dir: 'desc' };
  const query = { ...base, status: items('status').filter((s) => s.value < 90).map((s) => s.value).join(',') };
  const key = ['issues', 'board', query];
  const open = useQuery({ queryKey: key, queryFn: () => api.get<IssueList>('/api/issues' + qs(query)) });
  // closed issues: only those closed/updated in the last 30 days
  const closedQuery = { ...base, status: '90', updated_days: 30 };
  const closed = useQuery({
    queryKey: ['issues', 'board', closedQuery],
    queryFn: () => api.get<IssueList>('/api/issues' + qs(closedQuery)),
    enabled: showClosed,
  });
  const error = open.error || closed.error;
  const isLoading = open.isLoading;
  const data = open.data && {
    ...open.data,
    issues: [...open.data.issues, ...(showClosed ? (closed.data?.issues ?? []) : [])],
  };

  const move = useMutation({
    mutationFn: ({ id, status }: { id: number; status: number }) => api.patch(`/api/issues/${id}`, { status }),
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<IssueList>(key);
      if (prev) qc.setQueryData<IssueList>(key, { ...prev, issues: prev.issues.map((i) => (i.id === id ? { ...i, status } : i)) });
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(key, ctx.prev),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['issues'] });
      qc.invalidateQueries({ queryKey: ['myview'] });
    },
  });

  const people = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const i of data?.issues ?? []) m.set(i.handler_name ?? '', i.handler_name);
    return [...m.entries()].sort((a, b) => (a[0] === '' ? 1 : b[0] === '' ? -1 : a[0].localeCompare(b[0])));
  }, [data]);

  const visible = (data?.issues ?? []).filter((i) => {
    if (onlyMine && i.handler_id !== meta.user.id) return false;
    if (assignees.size && !assignees.has(i.handler_name ?? '')) return false;
    if (text) {
      const t = text.toLowerCase();
      if (!i.summary.toLowerCase().includes(t) && !String(i.id).includes(t.replace('#', ''))) return false;
    }
    return true;
  });

  const onDrop = (e: DragEvent, status: number) => {
    e.preventDefault();
    setOverCol(null);
    const id = Number(e.dataTransfer.getData('text/plain'));
    const issue = data?.issues.find((i) => i.id === id);
    if (issue && issue.status !== status) move.mutate({ id, status });
    setDragId(null);
  };

  const toggleAssignee = (k: string) => {
    const next = new Set(assignees);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    setAssignees(next);
  };

  return (
    <div className="page board-page">
      <div className="page-head">
        <h1>{t('View Issues')}</h1>
        <span className="subtle">
          {projectId ? project(projectId)?.name : t('All Projects')} · {t('drag cards between columns to change status')}
        </span>
        <div className="head-actions">
          <ViewSwitch current="board" />
        </div>
      </div>
      <div className="toolbar">
        <div className="search-inline">
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder={t('Search this board')} />
        </div>
        <div className="avatar-filter">
          {people.slice(0, 12).map(([k, name]) => (
            <button key={k} className={`avatar-toggle ${assignees.has(k) ? 'on' : ''}`} onClick={() => toggleAssignee(k)} title={name ?? t('Unassigned')}>
              <Avatar name={name} size={30} />
            </button>
          ))}
        </div>
        <button className={`btn subtle toggle ${onlyMine ? 'on' : ''}`} onClick={() => setOnlyMine(!onlyMine)}>
          {t('Only my issues')}
        </button>
        <button className={`btn subtle toggle ${showClosed ? 'on' : ''}`} onClick={() => setShowClosed(!showClosed)}>
          {t('Show closed (30 days)')}
        </button>
        {(onlyMine || assignees.size > 0 || text) && (
          <button
            className="link"
            onClick={() => {
              setOnlyMine(false);
              setAssignees(new Set());
              setText('');
            }}
          >
            {t('Clear filters')}
          </button>
        )}
      </div>
      <ErrorBox error={error || move.error} />
      {isLoading && <div className="subtle">{t('Loading board…')}</div>}
      {data && data.total > data.issues.length && (
        <div className="banner info">
          {t('Showing the first {shown} of {total} issues. Pick a project to narrow the board.', { shown: data.issues.length, total: data.total })}
        </div>
      )}
      <div className="board">
        {statuses.map((s) => {
          const col = visible.filter((i) => i.status === s.value);
          return (
            <div
              key={s.value}
              className={`board-col ${overCol === s.value ? 'over' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                if (overCol !== s.value) setOverCol(s.value);
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setOverCol(null);
              }}
              onDrop={(e) => onDrop(e, s.value)}
            >
              <div className="board-col-head">
                <span className={`col-dot ${statusClass(s.value)}`} />
                <span className="col-title">{label('status', s.value)}</span>
                <span className="col-count">{col.length}</span>
              </div>
              <div className="board-cards">
                {col.map((i) => (
                  <Card
                    key={i.id}
                    i={i}
                    dragging={dragId === i.id}
                    onDragStart={() => setDragId(i.id)}
                    onOpen={() => {
                      setLastList(col.map((x) => x.id));
                      navigate(`/issues/${i.id}`);
                    }}
                  />
                ))}
                {col.length === 0 && <div className="col-empty">{t('Drop issues here')}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Card({ i, dragging, onDragStart, onOpen }: { i: IssueHeader; dragging: boolean; onDragStart: () => void; onOpen: () => void }) {
  const issueRowColor = useIssueRowColor();
  const rowColor = issueRowColor(i.status);
  return (
    <div
      className={`board-card ${rowColor.className} ${dragging ? 'dragging' : ''}`.trim()}
      style={rowColor.style}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', String(i.id));
        e.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onOpen()}
    >
      <div className="card-summary">{i.summary}</div>
      {(i.category_name || i.view_state === 50) && (
        <div className="card-tags">
          {i.category_name && <span className="tag-mini">{i.category_name}</span>}
          {i.view_state === 50 && <span className="tag-mini private">{t('private')}</span>}
        </div>
      )}
      <div className="card-foot">
        <TypeIcon severity={i.severity} />
        <Link to={`/issues/${i.id}`} className="key" onClick={(e) => e.stopPropagation()}>
          {issueNo(i.id)}
        </Link>
        {i.target_version && <span className="tag-mini version">{i.target_version}</span>}
        <span className="spacer" />
        <PriorityIcon priority={i.priority} />
        <Avatar name={i.handler_name} size={24} />
      </div>
    </div>
  );
}
